/** Hook para gestión del sidebar de documentos de un equipo. */
import { useState, useEffect, useCallback } from 'react';
import { useToast } from '../components/contexts/ToastContext';
import apiService from '../services/apiService';

/**
 * Gestiona el estado y lógica del sidebar de documentos de un equipo.
 *
 * @returns {Object} Estado y handlers del dominio de documentos
 * @returns {Object|null} returns.selectedEquipoForDocs - Equipo con sidebar abierto
 * @returns {Function} returns.setSelectedEquipoForDocs - Setter (4 usos en JSX)
 * @returns {boolean} returns.uploadModalOpen - Estado del modal de upload
 * @returns {Object|null} returns.editingDoc - Documento en edición, null si es nuevo
 * @returns {boolean} returns.docLoading - Estado de carga de operaciones de documento
 * @returns {Array} returns.tiposDocumento - Catálogo de tipos de documento
 * @returns {Array} returns.documentosExistentes - Documentos del equipo seleccionado
 * @returns {Object|null} returns.docToDelete - Documento pendiente de confirmación de borrado
 * @returns {boolean} returns.versionHistoryOpen - Estado del modal de historial
 * @returns {Object|null} returns.docForHistory - Documento cuyo historial se muestra
 * @returns {boolean} returns.reportModalOpen - Estado del modal de reporte
 * @returns {Object|null} returns.docToReport - Documento a reportar
 * @returns {boolean} returns.reportLoading - Estado de carga del reporte
 * @returns {number} returns.docsRefreshTrigger - Counter que fuerza re-fetch de documentos
 * @returns {boolean} returns.isDragging - Estado de arrastre de archivos
 * @returns {File|null} returns.droppedFile - Archivo soltado via drag & drop (valor leído en JSX)
 * @returns {Function} returns.openUploadModal - Abre el modal para subir un documento nuevo
 * @returns {Function} returns.openEditDocModal - Abre el modal en modo edición para un documento existente
 * @returns {Function} returns.openVersionHistory - Abre el historial de versiones para un documento
 * @returns {Function} returns.closeUploadModal - Cierra el modal de upload y limpia editingDoc y droppedFile
 * @returns {Function} returns.closeVersionHistory - Cierra el historial; si needsRefresh=true, fuerza re-fetch de documentos
 * @returns {Function} returns.closeReportModal - Cierra el modal de reporte y limpia docToReport
 * @returns {Function} returns.requestDeleteDoc - Solicita confirmación de borrado para un documento
 * @returns {Function} returns.cancelDeleteDoc - Cancela el borrado y limpia docToDelete
 * @returns {Function} returns.handleDeleteDoc - Confirma y elimina el documento en docToDelete
 * @returns {Function} returns.handleReportClick - Abre el modal de reporte para un documento
 * @returns {Function} returns.handleReportSubmit - Envía el reporte de problema
 * @returns {Function} returns.handleDragEnter - Handler dragenter del sidebar
 * @returns {Function} returns.handleDragLeave - Handler dragleave del sidebar
 * @returns {Function} returns.handleDragOver - Handler dragover del sidebar
 * @returns {Function} returns.handleDrop - Handler drop del sidebar
 * @returns {Function} returns.handleUploadSave - Guarda documento (crear o actualizar)
 * @returns {Function} returns.getDocumentoFields - Genera config de campos para EntityModal
 */
function useEquipoDocumentos() {
  const { showToast } = useToast();

  const [selectedEquipoForDocs, setSelectedEquipoForDocs] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [docsRefreshTrigger, setDocsRefreshTrigger] = useState(0);
  const [editingDoc, setEditingDoc] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [documentosExistentes, setDocumentosExistentes] = useState([]);
  const [docToDelete, setDocToDelete] = useState(null);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [docForHistory, setDocForHistory] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [docToReport, setDocToReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [droppedFile, setDroppedFile] = useState(null);

  // Cargar tipos de documento para el select del modal
  useEffect(() => {
    /**
     * Carga catálogo de tipos de documento
     */
    const fetchTipos = async () => {
      const response = await apiService.getTiposDocumentoLista();
      if (response.success) {
        setTiposDocumento(response.data || []);
      }
    };
    fetchTipos();
  }, []);

  // Cargar documentos existentes cuando se selecciona un equipo o se actualiza la lista
  useEffect(() => {
    if (selectedEquipoForDocs) {
      apiService.getDocumentosEquipo({ equipo_id: selectedEquipoForDocs.id, limit: 1000 })
        .then(res => {
          if (res.success) {
            setDocumentosExistentes(res.data || []);
          }
        }).catch(err => console.warn('Error al cargar documentos:', err));
    } else {
      setDocumentosExistentes([]);
    }
  }, [selectedEquipoForDocs, docsRefreshTrigger]);

  /**
   * Elimina el documento en docToDelete
   * @returns {Promise<void>}
   */
  const handleDeleteDoc = async () => {
    if (!docToDelete) return;

    try {
      const response = await apiService.deleteDocumentoEquipo(docToDelete.id);
      if (response.success) {
        showToast('Documento eliminado', 'success');
        setDocsRefreshTrigger(prev => prev + 1);
      }
    } catch (error) {
      showToast('Error al eliminar documento', 'error');
    } finally {
      setDocToDelete(null);
    }
  };

  /**
   * Abre el modal para reportar un problema en un documento
   * @param {Object} doc - Documento a reportar
   */
  const handleReportClick = (doc) => {
    setDocToReport(doc);
    setReportModalOpen(true);
  };

  /**
   * Envía un reporte de problema en documento
   * @param {Object} data - { tipo_problema, descripcion }
   * @returns {Promise<void>}
   */
  const handleReportSubmit = async (data) => {
    setReportLoading(true);
    try {
      const response = await apiService.createDocumentoReporte({
        documento_equipo_id: docToReport?.id,
        ...data
      });
      if (response.success) {
        showToast('Reporte enviado exitosamente', 'success');
        setReportModalOpen(false);
        setDocToReport(null);
      } else {
        showToast(response.message || 'Error al enviar reporte', 'error');
      }
    } catch (error) {
      showToast('Error al enviar reporte', 'error');
    } finally {
      setReportLoading(false);
    }
  };

  /**
   * Handler dragenter del sidebar
   * @param {DragEvent} e
   */
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  /**
   * Handler dragleave del sidebar
   * @param {DragEvent} e
   */
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      setIsDragging(false);
    }
  }, []);

  /**
   * Handler dragover del sidebar
   * @param {DragEvent} e
   */
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  /**
   * Handler drop del sidebar; abre modal de upload con el archivo soltado
   * @param {DragEvent} e
   */
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!selectedEquipoForDocs?.id) {
      showToast('Error: No hay equipo seleccionado', 'error');
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(f =>
      ['application/pdf', 'image/jpeg', 'image/png'].includes(f.type)
    );

    if (validFiles.length === 0) {
      showToast('Solo se permiten archivos PDF, JPG, PNG', 'error');
      return;
    }

    if (validFiles.length > 1) {
      showToast('Arrastra solo 1 archivo a la vez. Usa el botón "Subir documento" para múltiples.', 'warning');
      return;
    }

    setDroppedFile(validFiles[0]);
    setEditingDoc(null);
    setUploadModalOpen(true);
  }, [selectedEquipoForDocs, showToast]);

  /**
   * Guarda documento (crear o actualizar) desde EntityModal de upload
   * @param {FormData} data - Datos del formulario
   * @returns {Promise<void>}
   */
  const handleUploadSave = async (data) => {
    setDocLoading(true);
    try {
      let response;
      if (editingDoc) {
        response = await apiService.updateDocumentoEquipo(editingDoc.id, data);
      } else {
        if (droppedFile) {
          data.set('archivo', droppedFile);
        }
        data.append('equipo_id', selectedEquipoForDocs.id);
        response = await apiService.uploadDocumentoEquipo(data);
      }

      if (response.success) {
        showToast(editingDoc ? 'Documento actualizado' : 'Documento subido', 'success');
        setDocsRefreshTrigger(prev => prev + 1);
        setUploadModalOpen(false);
        setEditingDoc(null);
        setDroppedFile(null);
      } else {
        showToast(response.message || 'Error', 'error');
      }
    } catch (error) {
      showToast('Error al procesar documento', 'error');
    } finally {
      setDocLoading(false);
    }
  };

  /**
   * Abre el modal para subir un documento nuevo
   */
  const openUploadModal = () => {
    setEditingDoc(null);
    setUploadModalOpen(true);
  };

  /**
   * Abre el modal en modo edición para un documento existente
   * @param {Object} doc - Documento a editar
   */
  const openEditDocModal = (doc) => {
    setEditingDoc(doc);
    setUploadModalOpen(true);
  };

  /**
   * Abre el historial de versiones para un documento
   * @param {Object} doc - Documento cuyo historial se muestra
   */
  const openVersionHistory = (doc) => {
    setDocForHistory(doc);
    setVersionHistoryOpen(true);
  };

  /**
   * Cierra el modal de upload y limpia editingDoc y droppedFile
   */
  const closeUploadModal = () => {
    setUploadModalOpen(false);
    setEditingDoc(null);
    setDroppedFile(null);
  };

  /**
   * Cierra el historial de versiones; si needsRefresh=true, fuerza re-fetch de documentos
   * @param {boolean} needsRefresh - Si true, incrementa docsRefreshTrigger
   */
  const closeVersionHistory = (needsRefresh) => {
    setVersionHistoryOpen(false);
    setDocForHistory(null);
    if (needsRefresh) setDocsRefreshTrigger(prev => prev + 1);
  };

  /**
   * Cierra el modal de reporte y limpia docToReport
   */
  const closeReportModal = () => {
    setReportModalOpen(false);
    setDocToReport(null);
  };

  /**
   * Solicita confirmación de borrado para un documento
   * @param {Object} doc - Documento a eliminar
   */
  const requestDeleteDoc = (doc) => {
    setDocToDelete(doc);
  };

  /**
   * Cancela el borrado y limpia docToDelete
   */
  const cancelDeleteDoc = () => {
    setDocToDelete(null);
  };

  /**
   * Genera config de campos para EntityModal de documentos.
   * Lee editingDoc del closure para filtrar tipos ya usados.
   *
   * @returns {Array<Object>} Configuración de campos para EntityModal
   */
  function getDocumentoFields() {
    const editingDocId = editingDoc?.id;
    const tiposUsados = documentosExistentes
      .filter(d => d.id !== editingDocId)
      .map(d => d.tipo_documento_id);
    const tiposDisponibles = tiposDocumento.filter(t => !tiposUsados.includes(t.id));

    const fields = [];

    // Solo mostrar selector de tipo en creación (no en edición - rompe trazabilidad)
    if (!editingDocId) {
      fields.push({
        name: 'tipo_documento_id',
        label: 'Tipo de Documento',
        type: 'select',
        required: true,
        options: tiposDisponibles.map(t => ({ value: t.id, label: t.nombre }))
      });
    }

    fields.push(
      {
        name: 'fecha_vencimiento',
        label: 'Fecha de Vencimiento',
        type: 'date',
        required: false
      },
      {
        name: 'archivo',
        label: 'Archivo',
        type: 'file',
        required: true,
        accept: '.pdf,.jpg,.jpeg,.png',
        hint: 'PDF, JPG, PNG (máx 5MB)',
        fileNameField: 'archivo_nombre'
      }
    );

    return fields;
  }

  return {
    selectedEquipoForDocs, setSelectedEquipoForDocs,
    uploadModalOpen,
    editingDoc,
    docLoading,
    tiposDocumento,
    documentosExistentes,
    docToDelete,
    versionHistoryOpen,
    docForHistory,
    reportModalOpen,
    docToReport,
    reportLoading,
    docsRefreshTrigger,
    isDragging,
    droppedFile,
    openUploadModal,
    openEditDocModal,
    openVersionHistory,
    closeUploadModal,
    closeVersionHistory,
    closeReportModal,
    requestDeleteDoc,
    cancelDeleteDoc,
    handleDeleteDoc,
    handleReportClick,
    handleReportSubmit,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleUploadSave,
    getDocumentoFields,
  };
}

export default useEquipoDocumentos;
